import React from "react";
import moment from "moment";
import { useQuery } from "@apollo/react-hooks";
import { gql } from "apollo-boost";

import StatusBadge from "designV2/components/StatusBadge";
import Button from "designV2/components/Button";
import { Skeleton } from "designV2/components/Skeleton";
import { graphqlUrl } from "utils/config";
import { DetailField, CopyableField, ImageStrip, Stars, humanize } from "../detailFields";
import "../superRide.css";

/* ------------------------------------------------------------------ *
 * GraphQL
 *
 * The passenger receipt asks for every field the type actually has. The
 * previous version requested nine of them but rendered a `CarType` row
 * that is not on TripReceipt at all — so that row always printed
 * "$unknown". The real fields are Transportation and the two CarType
 * price components, which were never fetched or shown.
 * ------------------------------------------------------------------ */

const GET_PASSENGER_RECEIPT = gql`
  query getTripReceiptForPassengerByAdmin($tripId: ID!) {
    getTripReceiptForPassengerByAdmin(tripId: $tripId) {
      tripStatus
      BaseFare
      Distance
      WaitTime
      BookingFee
      Transportation
      CarTypeDistancePrice
      CarTypeDurationPrice
      MoreOptions
      Promotion
      RequestFromFarPrice
      SubTotal
      HST
      Total
      CancelDescription
    }
  }
`;

const GET_DRIVER_RECEIPT = gql`
  query getTripReceiptForDriverByAdmin($tripId: ID!) {
    getTripReceiptForDriverByAdmin(tripId: $tripId) {
      driverTotalPrice
      tripDistance
      holdTimesInMinutes
      tripTimeInMinutes
      cancelReason
    }
  }
`;

const STATE_TONES = {
  PENDING: "neutral",
  RESERVED: "info",
  SEARCHING: "info",
  ACCEPTED: "info",
  ARRIVED: "warning",
  WAITING: "warning",
  PICKED_UP: "success",
  DESTINATION: "success",
  DRIVER_CANCELED: "danger",
  PASSENGER_CANCELED: "danger",
  PASSENGER_CANCELED_DURING_TRIP: "danger",
  FINISHED_DUE_TO_NOT_PAYING: "danger",
};

// The option keys the trip stores are shouty constants; these are what a
// person reading the dialog should see.
const OPTION_LABELS = {
  IN_HURRY: "In a hurry",
  TRIP_BAGS_WITH_ME: "Bags with me",
  RESERVED: "Reserved",
  TRIP_PET_WITH_CARRIER: "Pet, with carrier",
  TRIP_PET_WITHOUT_CARRIER: "Pet, no carrier",
  TRIP_WITH_INFANT: "With infant",
  TRIP_WELCOME_SIGN: "Welcome sign",
  TRIP_AIR_CONDITIONER: "Air conditioning",
  TRIP_DRIVER_ASSISTANT: "Driver assistant",
  WAIT_TIMES_IN_MINUTES: "Static wait time",
  DOOR_TO_DOOR_IN_BUILDING: "Door to door in building",
  ACCOMPANY_PARCEL: "Accompany parcel",
  PARCEL_PACKED: "Parcel packed",
  SIGNITURE_NEEDED: "Signature needed",
  ID_NEEDED: "ID needed",
  ORDERING_FOR_SOMEONE_ELSE: "Ordering for someone else",
};

const money = (value) =>
  value === null || value === undefined ? null : `$${Number(value).toFixed(2)}`;
const formatDateTime = (value) => (value ? moment(value).format("MMM D, YYYY [at] hh:mm A") : null);

const ReceiptRow = ({ label, value, total }) =>
  value === null || value === undefined ? null : (
    <div className={`superRideReceiptRow ${total ? "isTotal" : ""}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );

const CANCELLED_STATES = [
  "DRIVER_CANCELED",
  "PASSENGER_CANCELED",
  "PASSENGER_CANCELED_DURING_TRIP",
  "FINISHED_DUE_TO_NOT_PAYING",
];

/**
 * TripDetails - content-only, rendered inside a DialogShell by whoever
 * opens it (Trips, the finance report, a ticket's linked trip). The
 * previous version drew its own header and close button and was wrapped
 * in a bare MUI dialog at each call site.
 *
 * Ending a trip is confirmed by the parent rather than here, so the
 * confirmation is the same whether it is started from a row or from
 * inside this dialog. `onRequestEnd` is optional — call sites that
 * cannot end a trip (the finance report, tickets) simply omit it.
 */
const TripDetails = ({ trip, onRequestEnd }) => {
  const { data: passengerData, loading: passengerLoading } = useQuery(GET_PASSENGER_RECEIPT, {
    variables: { tripId: trip._id },
    errorPolicy: "all",
  });
  const { data: driverData, loading: driverLoading } = useQuery(GET_DRIVER_RECEIPT, {
    variables: { tripId: trip._id },
    errorPolicy: "all",
  });

  const passenger = passengerData && passengerData.getTripReceiptForPassengerByAdmin;
  const driverReceipt = driverData && driverData.getTripReceiptForDriverByAdmin;
  const loadingReceipts = passengerLoading || driverLoading;

  const mapUrl = trip.staticMapImageUrl ? `${graphqlUrl}${trip.staticMapImageUrl}` : null;
  const options = trip.optionsPriceDetails || [];
  const canEnd = Boolean(onRequestEnd) && !trip.ended && CANCELLED_STATES.indexOf(trip.state) === -1;

  return (
    <div className="superRideDetails">
      <div className="superRideDetailsTop">
        <StatusBadge
          status={STATE_TONES[trip.state] || "neutral"}
          label={humanize(trip.state) || "Unknown"}
        />
        <StatusBadge
          status={trip.ended ? "success" : "info"}
          label={trip.ended ? "Ended" : "In progress"}
          dot={false}
        />
        <span className="superRideTag">{humanize(trip.tripType)}</span>
        <span className="superRideDetailsHeadline">{money(trip.cost)}</span>
      </div>

      <div className="superRideGrid">
        <CopyableField label="Trip ID" value={trip._id} />
        <DetailField label="Rating" value={<Stars value={trip.rate} />} />
        <DetailField
          label="Driver"
          value={
            trip.driver ? (
              <React.Fragment>
                {trip.driver.fullName}
                <br />
                <span className="superRideMuted">{trip.driver.phoneNumber}</span>
              </React.Fragment>
            ) : null
          }
        />
        <DetailField
          label="Passenger"
          value={
            trip.passengerDetail ? (
              <React.Fragment>
                {trip.passengerDetail.fullName}
                <br />
                <span className="superRideMuted">{trip.passengerDetail.phoneNumber}</span>
              </React.Fragment>
            ) : null
          }
        />
        <DetailField
          label="Distance"
          value={trip.tripDistance ? `${trip.tripDistance} km` : null}
        />
        <DetailField label="Tip" value={money(trip.tipValue)} />
        <DetailField label="Wait time charge" value={money(trip.waitTimePrice)} />
        <DetailField label="Options charge" value={money(trip.optionsPrice)} />
        <DetailField label="Started" value={formatDateTime(trip.startDate)} />
        <DetailField label="Ended" value={formatDateTime(trip.endDate)} />
        <DetailField label="Created" value={formatDateTime(trip.createdAt)} />
      </div>

      {options.length > 0 && (
        <div className="superRideField">
          <span className="superRideLabel">Extras ({options.length})</span>
          <div className="superRideReceipt">
            {options.map((item, index) => (
              <ReceiptRow
                key={`${item.option}-${index}`}
                label={OPTION_LABELS[item.option] || humanize(item.option)}
                value={money(item.price)}
              />
            ))}
          </div>
        </div>
      )}

      <ImageStrip label="Route" urls={mapUrl ? [mapUrl] : []} wide />

      {loadingReceipts ? (
        <Skeleton height={120} />
      ) : (
        <div className="superRideReceipts">
          {passenger && (
            <div className="superRideReceipt">
              <span className="superRideReceiptTitle">Passenger receipt</span>
              <ReceiptRow label="Base fare" value={money(passenger.BaseFare)} />
              <ReceiptRow label="Distance" value={money(passenger.Distance)} />
              <ReceiptRow label="Transportation" value={money(passenger.Transportation)} />
              <ReceiptRow label="Car type — distance" value={money(passenger.CarTypeDistancePrice)} />
              <ReceiptRow label="Car type — duration" value={money(passenger.CarTypeDurationPrice)} />
              <ReceiptRow label="Wait time" value={money(passenger.WaitTime)} />
              <ReceiptRow label="Booking fee" value={money(passenger.BookingFee)} />
              <ReceiptRow label="More options" value={money(passenger.MoreOptions)} />
              <ReceiptRow label="Search area expansion" value={money(passenger.RequestFromFarPrice)} />
              <ReceiptRow label="Promotion" value={money(passenger.Promotion)} />
              <ReceiptRow label="Subtotal" value={money(passenger.SubTotal)} />
              <ReceiptRow label="HST" value={money(passenger.HST)} />
              <ReceiptRow label="Total" value={money(passenger.Total)} total />
              <ReceiptRow label="Cancellation" value={passenger.CancelDescription} />
            </div>
          )}

          {driverReceipt && (
            <div className="superRideReceipt">
              <span className="superRideReceiptTitle">Driver receipt</span>
              <ReceiptRow label="Distance" value={`${driverReceipt.tripDistance || 0} km`} />
              <ReceiptRow
                label="Trip time"
                value={
                  driverReceipt.tripTimeInMinutes ? `${driverReceipt.tripTimeInMinutes} min` : null
                }
              />
              <ReceiptRow
                label="Hold time"
                value={
                  driverReceipt.holdTimesInMinutes ? `${driverReceipt.holdTimesInMinutes} min` : null
                }
              />
              <ReceiptRow label="Driver payout" value={money(driverReceipt.driverTotalPrice)} total />
              <ReceiptRow label="Cancellation" value={driverReceipt.cancelReason} />
            </div>
          )}
        </div>
      )}

      {canEnd && (
        <div className="superRideFooter">
          <Button variant="danger" onClick={onRequestEnd}>
            End trip
          </Button>
        </div>
      )}
    </div>
  );
};

export default TripDetails;
